/**
 * FleetMap — sole MapLibre consumer for PMV Live Fleet Map.
 * Business pages must import from `@/components/pmv/map` only.
 */

import React, { useEffect, useRef, useState } from 'react';
import maplibregl, { type ExpressionSpecification, type Map, type GeoJSONSource } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  circleToPolygon,
  getFleetMapStyleUrl,
  type FleetMapBBox,
  type FleetMapGeofenceInput,
  type FleetMapMarkerInput,
} from './mapConfig';

const MARKERS_SOURCE = 'pmv-fleet-markers';
const GEOFENCE_SOURCE = 'pmv-fleet-geofences';

export type FleetMapProps = {
  markers: FleetMapMarkerInput[];
  geofences?: FleetMapGeofenceInput[];
  selectedId?: string | null;
  showGeofences?: boolean;
  /** Remount map with `key={String(satellite)}` from parent when toggling. */
  satellite?: boolean;
  fitToken?: number;
  height?: number | string;
  onSelect?: (id: string) => void;
  onViewportIdle?: (bbox: FleetMapBBox) => void;
  onMapError?: (message: string) => void;
  onMapReady?: () => void;
};

function markersToGeoJSON(markers: FleetMapMarkerInput[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: markers.map((m) => ({
      type: 'Feature',
      properties: {
        id: m.id,
        label: m.label || m.id,
        freshness: m.freshness || '',
        connectivity: m.connectivity || '',
        movement: m.movement || '',
        hasAttention: m.hasAttention ? 1 : 0,
      },
      geometry: { type: 'Point', coordinates: [m.lon, m.lat] },
    })),
  };
}

function geofencesToGeoJSON(geofences: FleetMapGeofenceInput[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const g of geofences) {
    if (
      g.geometry_type === 'CIRCLE' &&
      g.center_lat != null &&
      g.center_lon != null &&
      g.radius_m != null
    ) {
      const f = circleToPolygon(Number(g.center_lat), Number(g.center_lon), Number(g.radius_m));
      f.properties = { id: g.id, name: g.name || g.id, type: g.type || '' };
      features.push(f);
      continue;
    }
    if (g.geometry_type === 'POLYGON' && g.geometry_json?.coordinates?.[0]) {
      features.push({
        type: 'Feature',
        properties: { id: g.id, name: g.name || g.id, type: g.type || '' },
        geometry: {
          type: 'Polygon',
          coordinates: g.geometry_json.coordinates,
        },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

function colorExpr(): ExpressionSpecification {
  return [
    'case',
    ['==', ['get', 'hasAttention'], 1],
    '#c0392b',
    ['==', ['get', 'freshness'], 'LIVE'],
    '#1a7f37',
    ['==', ['get', 'freshness'], 'DELAYED'],
    '#b78103',
    '#6b7280',
  ];
}

function ensureLayers(map: Map) {
  if (!map.getSource(MARKERS_SOURCE)) {
    map.addSource(MARKERS_SOURCE, {
      type: 'geojson',
      data: markersToGeoJSON([]),
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });
  }
  if (!map.getSource(GEOFENCE_SOURCE)) {
    map.addSource(GEOFENCE_SOURCE, {
      type: 'geojson',
      data: geofencesToGeoJSON([]),
    });
  }
  if (!map.getLayer('pmv-geofence-fill')) {
    map.addLayer({
      id: 'pmv-geofence-fill',
      type: 'fill',
      source: GEOFENCE_SOURCE,
      paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.12 },
    });
    map.addLayer({
      id: 'pmv-geofence-line',
      type: 'line',
      source: GEOFENCE_SOURCE,
      paint: { 'line-color': '#2563eb', 'line-width': 2 },
    });
    map.addLayer({
      id: 'pmv-clusters',
      type: 'circle',
      source: MARKERS_SOURCE,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#1e3a5f',
        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 50, 26],
        'circle-opacity': 0.85,
      },
    });
    map.addLayer({
      id: 'pmv-cluster-count',
      type: 'symbol',
      source: MARKERS_SOURCE,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-size': 12,
      },
      paint: { 'text-color': '#ffffff' },
    });
    map.addLayer({
      id: 'pmv-unclustered',
      type: 'circle',
      source: MARKERS_SOURCE,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': colorExpr(),
        'circle-radius': 7,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
  }
}

export const FleetMap: React.FC<FleetMapProps> = ({
  markers,
  geofences = [],
  selectedId,
  showGeofences = true,
  satellite = false,
  fitToken = 0,
  height = 520,
  onSelect,
  onViewportIdle,
  onMapError,
  onMapReady,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const onSelectRef = useRef(onSelect);
  const onViewportIdleRef = useRef(onViewportIdle);
  const onMapErrorRef = useRef(onMapError);
  const onMapReadyRef = useRef(onMapReady);
  onSelectRef.current = onSelect;
  onViewportIdleRef.current = onViewportIdle;
  onMapErrorRef.current = onMapError;
  onMapReadyRef.current = onMapReady;

  useEffect(() => {
    if (!containerRef.current) return;

    let map: Map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: getFleetMapStyleUrl(satellite),
        center: [54.37, 24.45],
        zoom: 10,
      });
    } catch (e) {
      setFailed(true);
      onMapErrorRef.current?.((e as Error).message || 'Map failed to initialize');
      return;
    }

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    map.on('error', (e) => {
      onMapErrorRef.current?.(e?.error?.message || 'Map tile or style error');
    });

    const onLoad = () => {
      ensureLayers(map);
      map.on('click', 'pmv-clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['pmv-clusters'] });
        const clusterId = features[0]?.properties?.cluster_id;
        const source = map.getSource(MARKERS_SOURCE) as GeoJSONSource;
        if (clusterId == null || !features[0]) return;
        void source.getClusterExpansionZoom(Number(clusterId)).then((zoom) => {
          const coords = (features[0].geometry as GeoJSON.Point).coordinates as [number, number];
          map.easeTo({ center: coords, zoom });
        });
      });
      map.on('click', 'pmv-unclustered', (e) => {
        const id = e.features?.[0]?.properties?.id;
        if (id) onSelectRef.current?.(String(id));
      });
      map.on('mouseenter', 'pmv-unclustered', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'pmv-unclustered', () => {
        map.getCanvas().style.cursor = '';
      });
      map.on('moveend', () => {
        const b = map.getBounds();
        onViewportIdleRef.current?.({
          min_lat: b.getSouth(),
          max_lat: b.getNorth(),
          min_lon: b.getWest(),
          max_lon: b.getEast(),
        });
      });
      setReady(true);
      onMapReadyRef.current?.();
    };

    if (map.isStyleLoaded()) onLoad();
    else map.on('load', onLoad);

    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, [satellite]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !map.getSource(MARKERS_SOURCE)) return;
    (map.getSource(MARKERS_SOURCE) as GeoJSONSource).setData(markersToGeoJSON(markers));
  }, [markers, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !map.getSource(GEOFENCE_SOURCE)) return;
    (map.getSource(GEOFENCE_SOURCE) as GeoJSONSource).setData(
      geofencesToGeoJSON(showGeofences ? geofences : [])
    );
  }, [geofences, showGeofences, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !markers.length) return;
    try {
      const usable = markers.filter(
        (m) =>
          Number.isFinite(m.lat) &&
          Number.isFinite(m.lon) &&
          m.lat >= -90 &&
          m.lat <= 90 &&
          m.lon >= -180 &&
          m.lon <= 180
      );
      if (!usable.length) return;
      const bounds = new maplibregl.LngLatBounds(
        [usable[0].lon, usable[0].lat],
        [usable[0].lon, usable[0].lat]
      );
      for (let i = 1; i < usable.length; i++) {
        bounds.extend([usable[i].lon, usable[i].lat]);
      }
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 500 });
      }
    } catch {
      // Keep list usable if bounds are corrupt
    }
  }, [fitToken, ready, markers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !selectedId) return;
    const m = markers.find((x) => x.id === selectedId);
    if (
      m &&
      Number.isFinite(m.lat) &&
      Number.isFinite(m.lon) &&
      m.lat >= -90 &&
      m.lat <= 90
    ) {
      map.easeTo({ center: [m.lon, m.lat], zoom: Math.max(map.getZoom(), 12) });
    }
  }, [selectedId, markers, ready]);

  if (failed) {
    return (
      <div
        role="region"
        aria-label="Fleet map unavailable"
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
          border: '1px solid #e5e5e5',
          padding: 16,
          textAlign: 'center',
        }}
      >
        Map imagery is unavailable. Fleet location data remains available in list view.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      role="application"
      aria-label="Live fleet map"
      tabIndex={0}
      style={{
        height,
        width: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid #e5e5e5',
      }}
    />
  );
};

export default FleetMap;
